import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LineCounter from "./line";
import { useSocial } from "../hooks/useSocial";
import constantObj from "../constant/constant";
import {
  FaRocket, FaBolt, FaCrosshairs, FaChartLine, FaCandlestick,
  FaTelegramPlane, FaHeadset,
} from "react-icons/fa";
import { HiArrowRight } from "react-icons/hi";
import { MdCandlestickChart, MdTrendingUp, MdSpeed } from "react-icons/md";
import { BiTargetLock } from "react-icons/bi";
import { GiCrossedSwords } from "react-icons/gi";

const slides = [
  { headline: "UEA Trading Robot", sub: "Next-Gen Algorithmic Trading Software", accent: "Automated. Precise. Unstoppable." },
  { headline: "95% Win Rate", sub: "Advanced AI-Powered Strategy Engine", accent: "Trade Smarter, Earn Faster." },
  { headline: "Pass Prop Firms", sub: "FTMO, Tiger, E8 — All Challenges Passed", accent: "Your Journey to Financial Freedom Starts Here." },
  { headline: "24/7 Live Trading", sub: "MT4 • MT5 • TradeLocker • cTrader", accent: "Never Miss a Market Opportunity." },
];

function CandleChart() {
  const candles = [
    { h: 60, body: 30, up: true },{ h: 45, body: 20, up: false },
    { h: 80, body: 45, up: true },{ h: 55, body: 25, up: false },
    { h: 95, body: 55, up: true },{ h: 70, body: 35, up: true },
    { h: 50, body: 28, up: false },{ h: 100, body: 60, up: true },
  ];
  return (
    <div className="flex items-end gap-1.5 h-24 px-2">
      {candles.map((c, i) => (
        <motion.div key={i} className="flex flex-col items-center gap-0.5"
          initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }}
          transition={{ delay: i * 0.1, duration: 0.4 }} style={{ transformOrigin: "bottom" }}>
          <div className={`w-px h-2 ${c.up ? "bg-green-400" : "bg-red-400"}`} />
          <div className={`w-3 rounded-sm ${c.up ? "bg-green-400" : "bg-red-500"}`} style={{ height: `${c.body * 0.5}px` }} />
          <div className={`w-px h-2 ${c.up ? "bg-green-400" : "bg-red-400"}`} />
        </motion.div>
      ))}
    </div>
  );
}

const tickerItems = [
  { pair: "EUR/USD", price: "1.0842", change: "+0.23%", up: true },
  { pair: "GBP/USD", price: "1.2671", change: "+0.18%", up: true },
  { pair: "XAU/USD", price: "2,341.50", change: "-0.12%", up: false },
  { pair: "BTC/USD", price: "67,420", change: "+1.45%", up: true },
  { pair: "USD/JPY", price: "154.32", change: "-0.08%", up: false },
  { pair: "NAS100", price: "18,204", change: "+0.67%", up: true },
  { pair: "US30", price: "39,512", change: "+0.31%", up: true },
  { pair: "EUR/GBP", price: "0.8557", change: "-0.05%", up: false },
];

function TickerTape() {
  const doubled = [...tickerItems, ...tickerItems];
  return (
    <div className="w-full overflow-hidden bg-[#0a1628] border-y border-blue-500/10 py-2.5">
      <div className="ticker-track flex gap-12 whitespace-nowrap">
        {doubled.map((item, i) => (
          <span key={i} className="flex items-center gap-2 text-xs font-mono-code">
            <span className="text-slate-400 font-semibold tracking-wider">{item.pair}</span>
            <span className="text-white font-bold">{item.price}</span>
            <span className={`font-semibold ${item.up ? "text-green-400" : "text-red-400"}`}>{item.change}</span>
            <span className="text-slate-600 mx-2">|</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const strategies = [
  { name: "Price Action", icon: <MdTrendingUp className="text-xl" />, desc: "Pure market structure analysis", color: "#22c55e" },
  { name: "Trend + Pivot", icon: <BiTargetLock className="text-xl" />, desc: "Institutional level entries", color: "#3b82f6" },
  { name: "Candlestick", icon: <MdCandlestickChart className="text-xl" />, desc: "Pattern-based precision", color: "#22c55e" },
  { name: "Scalping", icon: <MdSpeed className="text-xl" />, desc: "High-frequency micro profits", color: "#3b82f6" },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const { socials } = useSocial();
  const telegram = socials.find((i) => i.label?.toLowerCase() === "telegram");
  const admin = socials.find((i) => i.label?.toLowerCase() === "admin");

  useEffect(() => {
    const t = setInterval(() => setCurrent((p) => (p + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex flex-col overflow-hidden grid-bg" style={{ background: "var(--uea-black)" }}>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, #22c55e, transparent)" }} />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, #3b82f6, transparent)" }} />

      <TickerTape />

      <div className="flex-1 flex flex-col lg:flex-row items-center justify-between max-w-7xl mx-auto w-full px-6 py-16 gap-12">
        <div className="lg:w-1/2 w-full text-center lg:text-left">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border text-xs font-semibold tracking-widest uppercase"
            style={{ borderColor: "rgba(34,197,94,0.3)", background: "rgba(34,197,94,0.05)", color: "#22c55e" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            UEA Trading Software — Live
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.h1 key={`h-${current}`} initial={{ opacity: 0, x: -60 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 60 }} transition={{ duration: 0.5 }}
              className="font-orbitron font-black text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
              <span className="text-white">{slides[current].headline.split(" ")[0]} </span>
              <span style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {slides[current].headline.split(" ").slice(1).join(" ")}
              </span>
            </motion.h1>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.p key={`s-${current}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl text-blue-300 font-semibold mb-3 font-exo">
              {slides[current].sub}
            </motion.p>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.p key={`a-${current}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.4, delay: 0.2 }}
              className="text-slate-400 mb-10 text-base">{slides[current].accent}</motion.p>
          </AnimatePresence>

          <div className="flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-4 mb-10">
            <motion.a href={telegram?.url || "#"}
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(34,197,94,0.4)" }} whileTap={{ scale: 0.97 }}
              className="font-orbitron text-sm tracking-widest uppercase font-bold px-8 py-4 rounded-full text-white w-full sm:w-auto text-center flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)" }}>
              <FaRocket /> Get Started Now
            </motion.a>
            <motion.a href={admin?.url || "#"}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
              className="font-orbitron text-sm tracking-widest uppercase font-bold px-8 py-4 rounded-full w-full sm:w-auto text-center border text-blue-400 hover:text-white transition-colors flex items-center justify-center gap-2"
              style={{ borderColor: "rgba(59,130,246,0.4)", background: "rgba(59,130,246,0.05)" }}>
              <FaHeadset /> Contact Admin
            </motion.a>
          </div>

          <div className="flex items-center lg:justify-start justify-center gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)}
                className={`transition-all duration-300 rounded-full ${i === current ? "w-8 h-2 bg-green-400" : "w-2 h-2 bg-slate-600 hover:bg-slate-400"}`} />
            ))}
          </div>
        </div>

        <div className="lg:w-1/2 w-full flex justify-center">
          <motion.div initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative w-full max-w-lg border-gradient rounded-2xl p-1"
            style={{ boxShadow: "0 0 60px rgba(34,197,94,0.15), 0 0 120px rgba(59,130,246,0.1)" }}>
            <div className="rounded-xl overflow-hidden" style={{ background: "var(--uea-card)" }}>
              <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: "rgba(59,130,246,0.1)", background: "rgba(10,22,40,0.8)" }}>
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-4 text-xs font-mono-code text-slate-400">uea_trading_bot.exe — Live Dashboard</span>
                <span className="ml-auto flex items-center gap-1 text-xs text-green-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> ACTIVE
                </span>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  {[{ label: "Win Rate", value: "95%", color: "#22c55e" }, { label: "Daily Profit", value: "+$1,240", color: "#3b82f6" }, { label: "DD Max", value: "3.2%", color: "#f59e0b" }].map((s, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                      className="rounded-lg p-3 text-center border" style={{ borderColor: "rgba(59,130,246,0.1)", background: "rgba(10,22,40,0.6)" }}>
                      <div className="text-lg font-orbitron font-bold" style={{ color: s.color }}>{s.value}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{s.label}</div>
                    </motion.div>
                  ))}
                </div>
                <div className="rounded-lg p-3 border" style={{ borderColor: "rgba(34,197,94,0.1)", background: "rgba(10,22,40,0.6)" }}>
                  <div className="text-[11px] text-slate-400 mb-2 font-mono-code">XAU/USD · M15 · EA Active</div>
                  <CandleChart />
                  <svg viewBox="0 0 300 30" className="w-full mt-1" style={{ height: "30px" }}>
                    <defs>
                      <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.8" />
                      </linearGradient>
                    </defs>
                    <motion.path d="M0,25 C50,20 80,15 120,10 S200,5 300,2" fill="none" stroke="url(#lineGrad)" strokeWidth="2"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, ease: "easeInOut" }} />
                  </svg>
                </div>
                <div className="space-y-1.5">
                  {[{ pair: "EUR/USD", type: "BUY", profit: "+$84.20", time: "09:42" }, { pair: "GBP/JPY", type: "SELL", profit: "+$127.50", time: "11:18" }, { pair: "XAU/USD", type: "BUY", profit: "+$340.00", time: "14:05" }].map((trade, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.15 }}
                      className="flex items-center justify-between text-xs py-1.5 px-2 rounded border"
                      style={{ borderColor: "rgba(34,197,94,0.08)", background: "rgba(34,197,94,0.03)" }}>
                      <span className="font-mono-code text-slate-300">{trade.pair}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${trade.type === "BUY" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>{trade.type}</span>
                      <span className="text-green-400 font-bold font-mono-code">{trade.profit}</span>
                      <span className="text-slate-500">{trade.time}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <LineCounter />
      <TradingStrategySection telegram={telegram} admin={admin} />
    </section>
  );
}

function TradingStrategySection({ telegram, admin }) {
  return (
    <div className="w-full py-20 px-6" style={{ background: "linear-gradient(180deg, var(--uea-black) 0%, var(--uea-dark) 100%)" }}>
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
        <div className="lg:w-1/2 w-full grid grid-cols-2 gap-4">
          {strategies.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              whileHover={{ scale: 1.04 }}
              className="p-5 rounded-xl border cursor-default"
              style={{ borderColor: "rgba(59,130,246,0.15)", background: "var(--uea-card)" }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ background: `${s.color}15`, color: s.color, border: `1px solid ${s.color}30` }}>
                {s.icon}
              </div>
              <div className="font-orbitron text-sm font-bold text-white mb-1">{s.name}</div>
              <div className="text-xs text-slate-400">{s.desc}</div>
            </motion.div>
          ))}
        </div>
        <div className="lg:w-1/2 w-full space-y-6">
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <span className="text-xs font-mono-code text-green-400 tracking-widest uppercase">// Trading Engine</span>
            <h2 className="font-orbitron font-black text-3xl lg:text-4xl text-white mt-3 leading-tight">
              THE UEA ROBOT{" "}
              <span style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                STRATEGY
              </span>
            </h2>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
            className="text-slate-400 leading-relaxed text-base">
            Robot built-in market scan and order detection with ultra-sensitive strategies.
            Incorporates advanced trading algorithms without dangerous methods — no martingale, no hedge.
            Each pair runs its own optimized set file for maximum precision.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
            className="flex flex-wrap gap-3">
            {["MT4", "MT5", "TradeLocker", "cTrader", "DXTrade", "MatchTrader"].map((p) => (
              <span key={p} className="text-xs font-mono-code px-3 py-1.5 rounded-full border text-blue-300"
                style={{ borderColor: "rgba(59,130,246,0.25)", background: "rgba(59,130,246,0.05)" }}>{p}</span>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4">
            <a href={telegram?.url || "#"}
              className="font-orbitron text-sm tracking-wider uppercase font-bold px-8 py-3.5 rounded-full text-white text-center transition-all hover:scale-105 flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)", boxShadow: "0 0 25px rgba(34,197,94,0.3)" }}>
              <FaTelegramPlane /> Try Demo
            </a>
            <a href={admin?.url || "#"}
              className="font-orbitron text-sm tracking-wider uppercase font-bold px-8 py-3.5 rounded-full text-center border text-slate-300 hover:text-white hover:border-green-400 transition-all flex items-center justify-center gap-2"
              style={{ borderColor: "rgba(148,163,184,0.2)" }}>
              <FaHeadset /> Contact Us
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
